import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/format';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ─── Fee Breakdown Display ────────────────────────────────────────────────────
function FeeBreakdown({ breakdown }) {
  if (!breakdown) return null;
  return (
    <div className="bg-[#F5F5F5] rounded-lg p-4 space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-[#777777]">Rent Amount</span>
        <span className="text-[#1B1B1B]">${(breakdown.rent_amount / 100).toFixed(2)}</span>
      </div>
      <div className="flex justify-between text-[#C8A800]">
        <span>+ Your Service Fee (1.5%)</span>
        <span>+${(breakdown.student_fee / 100).toFixed(2)}</span>
      </div>
      <div className="border-t border-[#E0E0E0] pt-2 flex justify-between font-bold">
        <span className="text-[#1B1B1B]">Total Charged to You</span>
        <span className="text-[#C8A800]">${(breakdown.total_charged / 100).toFixed(2)}</span>
      </div>
      <div className="border-t border-[#E0E0E0] pt-2 flex justify-between text-xs text-[#AAAAAA]">
        <span>Landlord receives</span>
        <span>${(breakdown.landlord_payout / 100).toFixed(2)}</span>
      </div>
      <div className="flex justify-between text-xs text-[#AAAAAA]">
        <span>NestHawk collects (4% total)</span>
        <span>${(breakdown.nesthawk_fee / 100).toFixed(2)}</span>
      </div>
    </div>
  );
}

// ─── Saved Lease Quick-Pay Card ───────────────────────────────────────────────
function SavedLeaseCard({ lease, onPay, onRemove }) {
  return (
    <div className="card p-5 border-2 border-[#C8A800]/40 bg-gradient-to-br from-[#FDFBF2] to-white max-w-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#C8A800]" />
          <span className="text-xs font-bold text-[#C8A800] tracking-widest uppercase">Saved Lease</span>
        </div>
        <button
          onClick={onRemove}
          className="text-xs text-[#AAAAAA] hover:text-red-400 transition-colors nav-link"
        >
          Not your lease? Remove
        </button>
      </div>
      <p className="font-bold text-[#1B1B1B] text-xl leading-tight mb-1">
        {lease.property_name || 'Your Property'}
      </p>
      <p className="text-sm text-[#777777] nav-link mb-5">
        ${lease.rent_amount.toLocaleString()} / month
      </p>
      <button onClick={onPay} className="btn-primary w-full py-3 text-sm">
        💳 Pay This Month's Rent
      </button>
    </div>
  );
}

// ─── Post-Payment Save Prompt ─────────────────────────────────────────────────
function SaveLeasePrompt({ propertyName, rentAmount, onSave, onSkip, saving }) {
  return (
    <div className="text-center py-4 space-y-5">
      <div className="w-16 h-16 bg-green-50 border-2 border-green-200 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
        </svg>
      </div>
      <div>
        <p className="font-bold text-[#1B1B1B] text-lg mb-1">Payment successful! 🎉</p>
        <p className="text-sm text-[#777777] nav-link">
          Save <strong>{propertyName || 'this property'}</strong> for quick one-tap payments next month?
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <button
          onClick={onSave}
          disabled={saving}
          className="btn-primary w-full py-3"
        >
          {saving ? 'Saving...' : '✓ Yes, save this lease'}
        </button>
        <button
          onClick={onSkip}
          className="btn-ghost w-full py-2.5 text-sm border border-[#DDDDDD]"
        >
          No thanks
        </button>
      </div>
    </div>
  );
}

// ─── Step 2: Checkout Form ────────────────────────────────────────────────────
// MUST be rendered inside <Elements stripe={...} options={{ clientSecret }}>
function CheckoutForm({ breakdown, onPaymentComplete, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payments?tab=history`,
      },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message);
      setLoading(false);
    } else {
      onPaymentComplete();
    }
  };

  return (
    <form onSubmit={handlePay} className="space-y-4">
      <FeeBreakdown breakdown={breakdown} />
      <div className="bg-[#F5F5F5] rounded-lg p-4">
        <PaymentElement />
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button type="submit" disabled={loading || !stripe} className="btn-primary w-full">
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Processing...
          </span>
        ) : `Pay $${breakdown ? (breakdown.total_charged / 100).toFixed(2) : '—'}`}
      </button>
      <button type="button" onClick={onBack} className="btn-ghost w-full text-sm">
        ← Change amount
      </button>
    </form>
  );
}

// ─── Pay Rent Form ────────────────────────────────────────────────────────────
// step: 1 (details) | 2 (stripe checkout) | 'save_prompt'
function PayRentForm({ defaultValues, savedLease, onSaveLease, onSuccess }) {
  const [form] = useState({
    landlord_id: defaultValues?.landlord_id || '',
    listing_id: defaultValues?.listing_id || '',
    sublease_id: defaultValues?.sublease_id || '',
    rent_amount: defaultValues?.rent_amount || '',
  });
  const [clientSecret, setClientSecret] = useState('');
  const [breakdown, setBreakdown] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rentAmount, setRentAmount] = useState(form.rent_amount);

  const propertyName = defaultValues?.property_name;
  const hasPrefilledLandlord = !!defaultValues?.landlord_id;

  // Auto-preview fee breakdown when rent is pre-filled from URL / saved lease
  useEffect(() => {
    if (rentAmount) previewFees(rentAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewFees = async (rentDollars) => {
    const amount = parseInt(rentDollars);
    if (!amount || amount <= 0) return;
    try {
      const res = await api.get(`/payments/fee-preview?rent=${amount * 100}`);
      setBreakdown(res.data);
    } catch {}
  };

  const createIntent = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/payments/create-intent', {
        landlord_id: form.landlord_id,
        listing_id: form.listing_id || undefined,
        sublease_id: form.sublease_id || undefined,
        rent_amount: parseInt(rentAmount) * 100,
      });
      setClientSecret(res.data.client_secret);
      setBreakdown(res.data.breakdown);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create payment');
    } finally {
      setLoading(false);
    }
  };

  // Called when Stripe confirms payment successfully
  const handlePaymentComplete = () => {
    // Check if this property is already the saved lease — if so skip the prompt
    const alreadySaved = savedLease && (
      (form.listing_id && savedLease.listing_id === form.listing_id) ||
      (form.sublease_id && savedLease.sublease_id === form.sublease_id) ||
      (!form.listing_id && !form.sublease_id && savedLease.landlord_id === form.landlord_id)
    );
    if (alreadySaved) {
      onSuccess();
    } else {
      setStep('save_prompt');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSaveLease({
      property_name: propertyName || '',
      rent_amount: parseInt(rentAmount),
      landlord_id: form.landlord_id,
      listing_id: form.listing_id || null,
      sublease_id: form.sublease_id || null,
    });
    setSaving(false);
    onSuccess();
  };

  // ── Save prompt (after successful payment) ──
  if (step === 'save_prompt') {
    return (
      <SaveLeasePrompt
        propertyName={propertyName}
        rentAmount={parseInt(rentAmount)}
        onSave={handleSave}
        onSkip={onSuccess}
        saving={saving}
      />
    );
  }

  // ── Step 2: Stripe checkout ──
  if (step === 2 && clientSecret) {
    return (
      <Elements
        stripe={stripePromise}
        options={{ clientSecret, appearance: { theme: 'stripe' } }}
      >
        <CheckoutForm
          breakdown={breakdown}
          onPaymentComplete={handlePaymentComplete}
          onBack={() => { setStep(1); setClientSecret(''); }}
        />
      </Elements>
    );
  }

  // ── Step 1: no listing — prompt to find one first ──
  if (!hasPrefilledLandlord) {
    return (
      <div className="text-center py-6 space-y-4">
        <div className="w-14 h-14 bg-[#F5F5F5] rounded-full flex items-center justify-center mx-auto">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7 text-[#AAAAAA]">
            <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
          </svg>
        </div>
        <div>
          <p className="font-semibold text-[#1B1B1B] mb-1">Find your listing first</p>
          <p className="text-sm text-[#777777] nav-link">
            Open the apartment or sublease listing and tap <strong>💳 Pay Rent</strong>. It pre-fills everything for you.
          </p>
        </div>
        <div className="flex flex-col gap-2 pt-2">
          <a href="/listings" className="btn-primary w-full py-3 text-sm text-center">Browse Apartments</a>
          <a href="/subleases" className="btn-ghost w-full py-3 text-sm text-center border border-[#DDDDDD]">Browse Subleases</a>
        </div>
      </div>
    );
  }

  // ── Step 1: pre-filled from listing or saved lease ──
  return (
    <div className="space-y-4">
      {propertyName && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <p className="text-xs text-blue-500 mb-0.5 font-medium">Paying rent for</p>
          <p className="font-semibold text-[#1B1B1B]">{propertyName}</p>
        </div>
      )}

      <div>
        <label className="label">Monthly Rent</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]">$</span>
          <input
            type="number"
            className="input pl-7"
            placeholder="800"
            value={rentAmount}
            onChange={e => setRentAmount(e.target.value)}
            onBlur={() => previewFees(rentAmount)}
          />
        </div>
      </div>

      {breakdown && <FeeBreakdown breakdown={breakdown} />}
      {error && <p className="text-red-500 text-sm">{error}</p>}

      <button
        onClick={createIntent}
        disabled={loading || !form.landlord_id || !rentAmount}
        className="btn-primary w-full"
      >
        {loading ? 'Processing...' : 'Continue to Payment'}
      </button>
    </div>
  );
}

// ─── Receive Payments (Stripe Connect onboarding) ─────────────────────────────
function LandlordConnect() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get('/payments/connect/status').then(res => {
      setStatus(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const startOnboarding = async () => {
    setConnecting(true);
    try {
      const res = await api.post('/payments/connect/onboard');
      window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start onboarding');
      setConnecting(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-8">
      <div className="w-6 h-6 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="card p-6">
      <h3 className="font-bold text-[#1B1B1B] text-lg mb-1">Receive Payments</h3>
      <p className="text-sm text-[#777777] mb-5 nav-link">Connect your bank account to accept rent or sublease payments directly.</p>

      <div className="bg-[#F5F5F5] rounded-lg p-4 mb-5 space-y-2 text-sm">
        <p className="font-semibold text-[#1B1B1B] mb-1">How payouts work</p>
        <div className="flex justify-between">
          <span className="text-[#777777]">Tenant pays (example: $800 rent)</span>
          <span className="text-[#1B1B1B]">$812.00</span>
        </div>
        <div className="flex justify-between text-red-500">
          <span>NestHawk fee (2.5% deducted from your payout)</span>
          <span>-$20.00</span>
        </div>
        <div className="border-t border-[#E0E0E0] pt-2 flex justify-between font-bold">
          <span className="text-[#1B1B1B]">You receive</span>
          <span className="text-[#C8A800]">$780.00</span>
        </div>
        <p className="text-[11px] text-[#AAAAAA] pt-1">The tenant pays a separate 1.5% fee on their end. Numbers above are based on an $800 rent example.</p>
      </div>

      {status?.onboarding_complete ? (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <svg className="w-6 h-6 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <div>
            <p className="font-semibold text-green-700">Bank account connected!</p>
            <p className="text-sm text-[#555555]">Payments will be deposited directly to your account.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-700 font-medium mb-1">Connect your bank to get paid</p>
            <p className="text-sm text-[#555555]">Anyone listing a place — landlords or students subleasing — can connect a bank account to receive payments. Takes 2–3 minutes.</p>
          </div>
          <button onClick={startOnboarding} disabled={connecting} className="btn-primary w-full">
            {connecting ? 'Redirecting to Stripe...' : 'Connect with Stripe'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Payments Page ─────────────────────────────────────────────────────────────
export default function Payments() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('pay');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [toast, setToast] = useState(null);

  // Saved lease state
  const [savedLease, setSavedLease] = useState(null);
  const [savedLeaseLoading, setSavedLeaseLoading] = useState(true);
  const [payingWithSaved, setPayingWithSaved] = useState(false);

  // Pre-fill values from URL when navigating from a listing detail page
  const prefilledLandlordId = searchParams.get('landlord_id');
  const prefilledListingId = searchParams.get('listing_id');
  const prefilledSubleaseId = searchParams.get('sublease_id');
  const prefilledRent = searchParams.get('rent');
  const prefilledPropertyName = searchParams.get('property_name');

  const urlDefaults = prefilledLandlordId
    ? {
        landlord_id: prefilledLandlordId,
        listing_id: prefilledListingId || '',
        sublease_id: prefilledSubleaseId || '',
        rent_amount: prefilledRent || '',
        property_name: prefilledPropertyName || '',
      }
    : null;

  // When user clicks "Pay Now" on the saved lease card, convert it to PayRentForm defaultValues
  const savedLeaseAsDefaults = savedLease
    ? {
        landlord_id: savedLease.landlord_id,
        listing_id: savedLease.listing_id || '',
        sublease_id: savedLease.sublease_id || '',
        rent_amount: String(savedLease.rent_amount),
        property_name: savedLease.property_name || '',
      }
    : null;

  // What the PayRentForm actually uses: URL params take priority (coming from listing),
  // then saved lease if user clicked "Pay Now", then null (shows find-listing prompt)
  const activeDefaults = urlDefaults || (payingWithSaved ? savedLeaseAsDefaults : null);

  useEffect(() => {
    const connectStatus = searchParams.get('connect');
    const tabParam = searchParams.get('tab');
    if (connectStatus === 'success') {
      setToast({ type: 'success', msg: 'Stripe Connect setup complete!' });
      setTimeout(() => setToast(null), 4000);
    }
    if (tabParam === 'connect') setTab('connect');
    if (tabParam === 'history') setTab('history');
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved lease from profile
  useEffect(() => {
    if (!user?.id) return;
    api.get(`/profile/${user.id}`)
      .then(res => setSavedLease(res.data.saved_lease || null))
      .catch(() => {})
      .finally(() => setSavedLeaseLoading(false));
  }, [user?.id]);

  async function fetchHistory() {
    try {
      const res = await api.get('/payments/history');
      setHistory(res.data);
    } catch {}
    setLoadingHistory(false);
  }

  const handleSaveLease = async (leaseData) => {
    try {
      const res = await api.put('/profile/saved-lease', leaseData);
      setSavedLease(res.data.saved_lease);
    } catch {}
  };

  const handleRemoveLease = async () => {
    if (!window.confirm('Remove your saved lease?')) return;
    try {
      await api.delete('/profile/saved-lease');
      setSavedLease(null);
      setPayingWithSaved(false);
    } catch {}
  };

  const handlePaySuccess = () => {
    setPayingWithSaved(false);
    fetchHistory();
    setTab('history');
    setToast({ type: 'success', msg: 'Payment successful!' });
    setTimeout(() => setToast(null), 4000);
  };

  const tabs = ['pay', 'connect', 'history'];

  return (
    <div className="page-container max-w-4xl mx-auto">
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-xl text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      <h1 className="section-title mb-6">Rent Payments</h1>

      <div className="flex gap-1 mb-6 bg-[#F0F0F0] p-1 rounded-lg w-fit">
        {tabs.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
              tab === t ? 'bg-[#C8A800] text-[#1B1B1B]' : 'text-[#666666] hover:text-[#1B1B1B]'
            }`}
          >
            {t === 'pay' ? 'Pay Rent' : t === 'connect' ? 'Receive Payments' : 'History'}
          </button>
        ))}
      </div>

      {tab === 'connect' && <LandlordConnect />}

      {tab === 'pay' && (
        <div className="flex flex-col gap-4 max-w-lg">

          {/* ── Saved lease card — shown when saved, not mid-payment, and not overridden by URL ── */}
          {!savedLeaseLoading && savedLease && !urlDefaults && !payingWithSaved && (
            <SavedLeaseCard
              lease={savedLease}
              onPay={() => setPayingWithSaved(true)}
              onRemove={handleRemoveLease}
            />
          )}

          {/* ── Divider when saved card is visible alongside a "pay different property" option ── */}
          {!savedLeaseLoading && savedLease && !urlDefaults && !payingWithSaved && (
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-[#E8E8E8]" />
              <span className="text-xs text-[#AAAAAA] nav-link">or pay a different property</span>
              <div className="flex-1 border-t border-[#E8E8E8]" />
            </div>
          )}

          {/* ── Pay form ── */}
          <div className="card p-6">
            {payingWithSaved && (
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-bold text-[#1B1B1B] text-lg">Pay Rent Securely</h2>
                <button
                  onClick={() => setPayingWithSaved(false)}
                  className="text-sm text-[#777777] hover:text-[#1B1B1B] transition-colors nav-link"
                >
                  ← Back
                </button>
              </div>
            )}
            {!payingWithSaved && (
              <h2 className="font-bold text-[#1B1B1B] text-lg mb-5">Pay Rent Securely</h2>
            )}
            <PayRentForm
              key={`${urlDefaults?.landlord_id}-${payingWithSaved}`}
              defaultValues={activeDefaults}
              savedLease={savedLease}
              onSaveLease={handleSaveLease}
              onSuccess={handlePaySuccess}
            />
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div>
          {loadingHistory ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-20">
              <svg className="w-10 h-10 text-[#DDDDDD] mb-4 mx-auto" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
              <p className="text-xl font-bold text-[#1B1B1B] mb-2">No payments yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map(p => (
                <div key={p.id} className="card p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full ${
                        p.status === 'succeeded' ? 'bg-green-500' :
                        p.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                      }`} />
                      <span className="font-medium text-[#1B1B1B] capitalize">{p.property_name || 'Rent Payment'}</span>
                    </div>
                    <p className="text-sm text-[#666666]">
                      {user?.id === p.student_id ? `→ ${p.landlord_name}` : `← ${p.student_name}`}
                    </p>
                    <p className="text-xs text-[#AAAAAA]">{formatDate(p.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#C8A800]">
                      {user?.id === p.student_id
                        ? `-$${(p.total_charged / 100).toFixed(2)}`
                        : `+$${(p.landlord_payout / 100).toFixed(2)}`}
                    </p>
                    {user?.id !== p.student_id && p.landlord_fee > 0 && (
                      <p className="text-xs text-red-400">-${(p.landlord_fee / 100).toFixed(2)} NestHawk fee</p>
                    )}
                    <p className="text-xs text-[#999999] capitalize">{p.status}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
